/**
 * Populating Gravity Forms hidden fields in the browser.
 *
 * This is the FALLBACK path, not the authoritative one. gform_pre_submission overwrites
 * these values server-side on POST with whatever the server holds. What the browser writes
 * matters in exactly one case, and it is an important one: a visitor whose cookie is
 * blocked entirely, where the client-stamped values are the only data there is. That is
 * why the server's overwrite is conditional on holding a non-empty value.
 *
 * TWO RENDER EVENTS ARE BOUND, BECAUSE GRAVITY FORMS CHANGED. Up to GF 2.8 the only signal
 * was the jQuery event `gform_post_render`; GF 2.9 added a native `gform/postRender`.
 * Binding only the native one silently does nothing on older installs; binding only the
 * jQuery one requires jQuery and does nothing on a 2.9 site that has dropped it. Both are
 * bound, and a guard stops a form being populated twice when both fire.
 *
 * These events are also how multi-page forms, validation-error redisplay and AJAX-embedded
 * forms work at all -- in each case the markup is replaced after initial page load, so a
 * one-shot population on DOMContentLoaded would leave the new fields empty.
 */

/**
 * Reads the parameter-to-input-id map that PHP emitted next to the form.
 *
 * Gravity Forms puts a field's parameter name nowhere in the rendered markup, so this map
 * is the only reliable way to know which input carries which canonical parameter. It is
 * generated from the same form object that produced the markup, so a cached form and a
 * cached map always agree.
 *
 * @param {number|string} formId Form id.
 * @param {object}        doc    Document.
 * @return {object} Map of canonical parameter to input element id.
 */
export function readFieldMap( formId, doc ) {
	const node = doc.querySelector( `script.cc-attr-field-map[data-form-id="${ formId }"]` );

	if ( ! node ) {
		return {};
	}

	try {
		const parsed = JSON.parse( node.textContent || '{}' );
		return parsed && typeof parsed.fields === 'object' && parsed.fields ? parsed.fields : {};
	} catch {
		return {};
	}
}

/**
 * Finds the input for a canonical parameter.
 *
 * Tries the emitted map first, then a CSS-class fallback. The fallback exists for
 * hand-built fields -- a field an agency added in the form editor years ago, carrying the
 * right class but not created by this plugin.
 *
 * @param {string} param  Canonical parameter name.
 * @param {object} map    Map from readFieldMap().
 * @param {object} scope  Element to search within.
 * @return {Element|null} The input, or null.
 */
export function findInput( param, map, scope ) {
	const id = map[ param ];

	if ( id ) {
		// CSS.escape exists in browsers but not in every runtime this module is loaded in;
		// referencing an undeclared CSS identifier directly would throw a ReferenceError.
		const escaped = typeof CSS !== 'undefined' && CSS.escape ? CSS.escape( id ) : id;
		const byId = scope.querySelector( `#${ escaped }` );

		if ( byId ) {
			return byId;
		}
	}

	// CSS-class fallback for hand-built fields.
	const cssName = param.replace( /_/g, '-' );

	return (
		scope.querySelector( `.cc-attr-field--${ cssName } input` ) ||
		scope.querySelector( `input[name="${ param }"]` )
	);
}

/**
 * Writes attribution values into one form's inputs.
 *
 * @param {Element} formElement The form element.
 * @param {object}  values      Canonical parameter to value.
 * @param {object}  doc         Document.
 * @return {number} How many inputs were written.
 */
export function populateForm( formElement, values, doc ) {
	if ( ! formElement || ! values ) {
		return 0;
	}

	const formId = formElement.getAttribute( 'data-formid' ) || extractFormId( formElement.id );
	const map = readFieldMap( formId, doc );

	let written = 0;

	for ( const [ param, value ] of Object.entries( values ) ) {
		if ( value === '' || value === null || value === undefined ) {
			// Never blank a field with an empty value; the same rule the server follows.
			continue;
		}

		const input = findInput( param, map, formElement );

		if ( input ) {
			input.value = value;
			written++;
		}
	}

	return written;
}

/**
 * Extracts the numeric form id from a Gravity Forms wrapper or form element id.
 *
 * @param {string} elementId Element id such as gform_7 or gform_wrapper_7.
 * @return {string} Form id, or an empty string.
 */
export function extractFormId( elementId ) {
	const match = /(?:gform|gform_wrapper)_(\d+)/.exec( String( elementId || '' ) );

	return match ? match[ 1 ] : '';
}

/**
 * Populates every Gravity Form currently in the document.
 *
 * @param {object} values Canonical parameter to value.
 * @param {object} doc    Document.
 * @return {number} How many forms were touched.
 */
export function populateAllForms( values, doc ) {
	const forms = doc.querySelectorAll( 'form[id^="gform_"]' );
	let touched = 0;

	for ( const form of forms ) {
		if ( populateForm( form, values, doc ) > 0 ) {
			touched++;
		}
	}

	return touched;
}

/**
 * Binds both Gravity Forms render events.
 *
 * @param {object}   options            Options.
 * @param {object}   options.doc        Document.
 * @param {object}   options.win        Window.
 * @param {Function} options.getValues  Returns the current attribution values.
 * @return {void}
 */
export function bindFormRender( { doc, win, getValues } ) {
	const populate = () => {
		const values = getValues();

		if ( values ) {
			populateAllForms( values, doc );
		}
	};

	// GF 2.9+ dispatches a native event on document.
	doc.addEventListener( 'gform/postRender', populate );

	// GF < 2.9 triggers a jQuery event, which a native listener will NOT observe --
	// jQuery's .trigger() does not dispatch a real DOM event. Bound only when jQuery is
	// already present; never loaded, never depended on.
	if ( typeof win.jQuery === 'function' ) {
		try {
			win.jQuery( doc ).on( 'gform_post_render', populate );
		} catch {
			// A jQuery too old to accept this call is not a reason to fail the page.
		}
	}

	// And once for forms already in the document at script execution time.
	populate();
}
